import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PortalLayout from '../PortalLayout';
import { employeeV2Service } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileTabSelect from '../../../components/common/MobileTabSelect';
import {
    C, FONT, avatarColor, initials,
    cardStyle, labelStyle, inputStyle, primaryButtonStyle, secondaryButtonStyle,
} from './shared';
import TabPersonalInfo from './TabPersonalInfo';
import TabEmployment from './TabEmployment';
import TabPayroll from './TabPayroll';
import TabSchedule from './TabSchedule';
import TabAccess from './TabAccess';

const TABS = [
    { key: 'personal', translationKey: 'tab_personal_info' },
    { key: 'employment', translationKey: 'tab_employment' },
    { key: 'payroll', translationKey: 'tab_payroll' },
    { key: 'schedule', translationKey: 'tab_schedule' },
    { key: 'access', translationKey: 'tab_access' },
];

const EMPLOYMENT_KEYS = { full_time: 'full_time_label', part_time: 'part_time_label', contractor: 'contractor' };
const PAY_FREQUENCY_KEYS = { daily: 'pay_frequency_daily', weekly: 'pay_frequency_weekly', monthly: 'pay_frequency_monthly' };

// ─── Edit Personal Info Modal ────────────────────────────────────────────────

const LANGUAGES = [{ value: 'en', labelKey: 'english' }, { value: 'ar', labelKey: 'arabic' }];

const sectionLabel = (text) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 8, marginBottom: 4 }}>{text}</div>
);

const EditPersonalInfoModal = ({ employee, onClose, onSaved, t }) => {
    const p = employee.personal || {};
    const [form, setForm] = useState({
        phone: p.phone || '',
        email: p.email || '',
        preferred_language: p.preferred_language || 'en',
        date_of_birth: p.date_of_birth || '',
        address: p.address || '',
        emergency_contact_name: p.emergency_contact_name || '',
        emergency_contact_relationship: p.emergency_contact_relationship || '',
        emergency_contact_phone: p.emergency_contact_phone || '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setError('');
            await employeeV2Service.updatePersonal(employee.employee_id, {
                phone: form.phone || null,
                email: form.email || null,
                preferred_language: form.preferred_language,
                date_of_birth: form.date_of_birth || null,
                address: form.address || null,
                emergency_contact_name: form.emergency_contact_name || null,
                emergency_contact_relationship: form.emergency_contact_relationship || null,
                emergency_contact_phone: form.emergency_contact_phone || null,
            });
            await onSaved();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.detail || t('failed_to_save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
            onKeyDown={(e) => e.key === 'Escape' && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 540, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: FONT }}>
                <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{t('edit_personal_info')}</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '10px 14px', fontSize: 13, color: C.redText }}>{error}</div>}

                    {sectionLabel(t('contact_info'))}
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                            <label style={labelStyle}>{t('phone')}</label>
                            <input style={inputStyle} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+44 7700 000000" />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('email')}</label>
                            <input style={inputStyle} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="name@example.com" />
                        </div>
                    </div>
                    <div>
                        <label style={labelStyle}>{t('preferred_language')}</label>
                        <select style={inputStyle} value={form.preferred_language} onChange={(e) => set('preferred_language', e.target.value)}>
                            {LANGUAGES.map(l => <option key={l.value} value={l.value}>{t(l.labelKey)}</option>)}
                        </select>
                    </div>

                    {sectionLabel(t('personal_info'))}
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                            <label style={labelStyle}>{t('date_of_birth')}</label>
                            <input style={inputStyle} type="date" value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('address')}</label>
                            <input style={inputStyle} value={form.address} onChange={(e) => set('address', e.target.value)} placeholder={t('address_placeholder')} />
                        </div>
                    </div>

                    {sectionLabel(t('emergency_contact'))}
                    <div className="portal-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                        <div>
                            <label style={labelStyle}>{t('name')}</label>
                            <input style={inputStyle} value={form.emergency_contact_name} onChange={(e) => set('emergency_contact_name', e.target.value)} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('relationship')}</label>
                            <input style={inputStyle} value={form.emergency_contact_relationship} onChange={(e) => set('emergency_contact_relationship', e.target.value)} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('phone')}</label>
                            <input style={inputStyle} value={form.emergency_contact_phone} onChange={(e) => set('emergency_contact_phone', e.target.value)} />
                        </div>
                    </div>
                </div>

                <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.borderLight}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={secondaryButtonStyle}>{t('cancel')}</button>
                    <button onClick={handleSubmit} disabled={saving}
                        style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1, cursor: saving ? 'default' : 'pointer' }}>
                        {saving ? t('saving') : t('save_changes')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Edit Employment Modal ──────────────────────────────────────────────────

const DEPARTMENT_KEYS = ['kitchen', 'front_of_house', 'bar', 'management', 'delivery', 'other'];
const DEPARTMENT_VALUES = ['Kitchen', 'Front of House', 'Bar', 'Management', 'Delivery', 'Other'];
const EMPLOYMENT_TYPES = [
    { value: 'full_time', key: 'full_time_label' },
    { value: 'part_time', key: 'part_time_label' },
    { value: 'contractor', key: 'contractor' },
];
const PAY_TYPES = [
    { value: 'salary', key: 'pay_type_salary' },
    { value: 'hourly', key: 'pay_type_hourly' },
    { value: 'hybrid', key: 'pay_type_hybrid' },
];
const PAY_FREQUENCIES = [
    { value: 'monthly', key: 'pay_frequency_monthly' },
    { value: 'weekly', key: 'pay_frequency_weekly' },
    { value: 'daily', key: 'pay_frequency_daily' },
];
const TIP_PARTICIPATIONS = [
    { value: 'none', key: 'tip_participation_none' },
    { value: 'direct_only', key: 'tip_participation_direct_only' },
    { value: 'pool_member', key: 'tip_participation_pool_member' },
];

const EditEmploymentModal = ({ employee, onClose, onSaved, t }) => {
    const e = employee.employment || {};
    const c = employee.compensation || {};
    const [form, setForm] = useState({
        role: employee.role || '',
        department: employee.department || '',
        employment_type: e.employment_type || 'full_time',
        start_date: e.start_date || '',
        end_date: e.end_date || '',
        termination_reason: e.termination_reason || '',
        pay_type: c.pay_type || 'salary',
        base_amount: c.base_amount ?? 0,
        pay_frequency: c.pay_frequency || 'monthly',
        tip_participation: c.tip_participation || 'none',
        tip_weight: c.tip_weight ?? 1,
        is_overtime_eligible: !!c.is_overtime_eligible,
        overtime_multiplier: c.overtime_multiplier ?? 1.5,
        is_bonus_eligible: !!c.is_bonus_eligible,
        reason: '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const handleSubmit = async () => {
        if (!form.role.trim()) return setError(t('role_is_required'));
        try {
            setSaving(true);
            setError('');
            await employeeV2Service.updateCore(employee.employee_id, {
                role: form.role.trim(),
                department: form.department || null,
            });
            await employeeV2Service.updateEmployment(employee.employee_id, {
                employment_type: form.employment_type,
                start_date: form.start_date || null,
                end_date: form.end_date || null,
                termination_reason: form.termination_reason || null,
            });
            await employeeV2Service.updateCompensation(employee.employee_id, {
                pay_type: form.pay_type,
                base_amount: parseFloat(form.base_amount) || 0,
                pay_frequency: form.pay_frequency,
                tip_participation: form.tip_participation,
                tip_weight: parseFloat(form.tip_weight) || 0,
                is_overtime_eligible: !!form.is_overtime_eligible,
                overtime_multiplier: parseFloat(form.overtime_multiplier) || 1.5,
                is_bonus_eligible: !!form.is_bonus_eligible,
                reason: form.reason || null,
            });
            await onSaved();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.detail || t('failed_to_save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
            onKeyDown={(e) => e.key === 'Escape' && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 580, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: FONT }}>
                <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{t('edit_employment')}</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '10px 14px', fontSize: 13, color: C.redText }}>{error}</div>}

                    {sectionLabel(t('employment'))}
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                            <label style={labelStyle}>{t('role')} *</label>
                            <input style={inputStyle} value={form.role} onChange={(ev) => set('role', ev.target.value)} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('department')}</label>
                            <select style={inputStyle} value={form.department} onChange={(ev) => set('department', ev.target.value)}>
                                <option value="">{t('select_department')}</option>
                                {DEPARTMENT_VALUES.map((d, i) => <option key={d} value={d}>{t(DEPARTMENT_KEYS[i])}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('employment_type')}</label>
                            <select style={inputStyle} value={form.employment_type} onChange={(ev) => set('employment_type', ev.target.value)}>
                                {EMPLOYMENT_TYPES.map(et => <option key={et.value} value={et.value}>{t(et.key)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('start_date')}</label>
                            <input style={inputStyle} type="date" value={form.start_date} onChange={(ev) => set('start_date', ev.target.value)} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('end_date')}</label>
                            <input style={inputStyle} type="date" value={form.end_date} onChange={(ev) => set('end_date', ev.target.value)} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('termination_reason')}</label>
                            <input style={inputStyle} value={form.termination_reason} onChange={(ev) => set('termination_reason', ev.target.value)} />
                        </div>
                    </div>

                    {sectionLabel(t('compensation_structure'))}
                    <div className="portal-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                        <div>
                            <label style={labelStyle}>{t('pay_type')}</label>
                            <select style={inputStyle} value={form.pay_type} onChange={(ev) => set('pay_type', ev.target.value)}>
                                {PAY_TYPES.map(p => <option key={p.value} value={p.value}>{t(p.key)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('base_amount')}</label>
                            <input style={inputStyle} type="number" min="0" step="0.01" value={form.base_amount} onChange={(ev) => set('base_amount', ev.target.value)} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('pay_frequency')}</label>
                            <select style={inputStyle} value={form.pay_frequency} onChange={(ev) => set('pay_frequency', ev.target.value)}>
                                {PAY_FREQUENCIES.map(p => <option key={p.value} value={p.value}>{t(p.key)}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                            <label style={labelStyle}>{t('tip_participation')}</label>
                            <select style={inputStyle} value={form.tip_participation} onChange={(ev) => set('tip_participation', ev.target.value)}>
                                {TIP_PARTICIPATIONS.map(p => <option key={p.value} value={p.value}>{t(p.key)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('tip_weight')}</label>
                            <input style={inputStyle} type="number" min="0" step="0.1" value={form.tip_weight} onChange={(ev) => set('tip_weight', ev.target.value)} />
                        </div>
                    </div>
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text, cursor: 'pointer' }}>
                            <input type="checkbox" checked={form.is_overtime_eligible} onChange={(ev) => set('is_overtime_eligible', ev.target.checked)} />
                            {t('overtime_eligible')}
                        </label>
                        <div>
                            <label style={labelStyle}>{t('overtime_multiplier')}</label>
                            <input style={{ ...inputStyle, opacity: form.is_overtime_eligible ? 1 : 0.5 }} type="number" min="1" step="0.05" disabled={!form.is_overtime_eligible}
                                value={form.overtime_multiplier} onChange={(ev) => set('overtime_multiplier', ev.target.value)} />
                        </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text, cursor: 'pointer' }}>
                        <input type="checkbox" checked={form.is_bonus_eligible} onChange={(ev) => set('is_bonus_eligible', ev.target.checked)} />
                        {t('bonus_eligible')}
                    </label>
                    <div>
                        <label style={labelStyle}>{t('reason_for_change')}</label>
                        <input style={inputStyle} value={form.reason} onChange={(ev) => set('reason', ev.target.value)} placeholder={t('optional')} />
                    </div>
                </div>

                <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.borderLight}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={secondaryButtonStyle}>{t('cancel')}</button>
                    <button onClick={handleSubmit} disabled={saving}
                        style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1, cursor: saving ? 'default' : 'pointer' }}>
                        {saving ? t('saving') : t('save_changes')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Edit Access Modal ──────────────────────────────────────────────────────

const LANG_OPTIONS = [{ value: 'en', labelKey: 'english' }, { value: 'ar', labelKey: 'arabic' }];

const EditAccessModal = ({ employee, onClose, onSaved, t }) => {
    const a = employee.access || {};
    // The API never returns the PIN (only access.has_pin), so the box starts
    // blank and blank means "leave the existing PIN alone".
    const [form, setForm] = useState({
        is_cashier: !!a.is_cashier,
        pin_code: '',
        preferred_language: employee.personal?.preferred_language || 'en',
        // Seeded from the CURRENT level — defaulting to 'staff' would silently
        // demote a manager on the next save of an unrelated field.
        system_role: employee.system_role || 'staff',
    });
    const [showPin, setShowPin] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const handleSubmit = async () => {
        // A cashier needs a PIN — but one they already have counts, and we can
        // no longer see it, so only demand one when none is set yet.
        if (form.is_cashier && !form.pin_code && !a.has_pin) return setError(t('pin_required_for_cashier'));
        if (form.pin_code && !/^\d{6}$/.test(form.pin_code)) return setError(t('pin_must_be_6_digits'));
        try {
            setSaving(true);
            setError('');
            // Omit pin_code entirely when untouched: the backend uses
            // exclude_unset, so sending null here would wipe the stored PIN.
            const accessPayload = { is_cashier: form.is_cashier };
            if (form.pin_code) accessPayload.pin_code = form.pin_code;
            // Only when CHANGED: the backend gates any non-default system_role
            // on portal:roles, so sending it unchanged would make routine
            // access edits fail for Managers who lack that permission.
            if ((form.system_role || 'staff') !== (employee.system_role || 'staff')) {
                accessPayload.system_role = form.system_role;
            }
            await employeeV2Service.updateAccess(employee.employee_id, accessPayload);
            if (form.preferred_language !== (employee.personal?.preferred_language || 'en')) {
                await employeeV2Service.updatePersonal(employee.employee_id, {
                    preferred_language: form.preferred_language,
                });
            }
            await onSaved();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.detail || t('failed_to_save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
            onKeyDown={(e) => e.key === 'Escape' && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 520, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: FONT }}>
                <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{t('edit_access')}</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '10px 14px', fontSize: 13, color: C.redText }}>{error}</div>}

                    {sectionLabel(t('pos_access'))}
                    <div onClick={() => set('is_cashier', !form.is_cashier)}
                        style={{ background: form.is_cashier ? C.greenLight : C.bg, border: `1px solid ${form.is_cashier ? '#bbf7d0' : C.border}`, borderRadius: 8, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{t('cashier_access')}</div>
                            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{t('can_login_via_pin')}</div>
                        </div>
                        <div style={{ width: 40, height: 22, borderRadius: 11, background: form.is_cashier ? C.green : '#cbd5e1', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                            <div style={{ position: 'absolute', top: 3, left: form.is_cashier ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: C.white, transition: 'left 0.2s' }} />
                        </div>
                    </div>
                    {/* POS access level. Separate from the Role field on the
                        Employment tab, which is a free-text job title the POS
                        never reads: the station-settings gate tests the JWT
                        role claim, minted from system_role. A "Manager" left
                        on Staff here cannot configure a till. */}
                    <div>
                        <label style={labelStyle}>{t('pos_access_level')}</label>
                        <select
                            style={inputStyle}
                            data-testid="access-system-role"
                            value={form.system_role}
                            onChange={(e) => set('system_role', e.target.value)}
                        >
                            <option value="staff">{t('pos_access_staff')}</option>
                            <option value="manager">{t('pos_access_manager')}</option>
                        </select>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
                            {form.system_role === 'manager'
                                ? t('pos_access_manager_hint')
                                : t('pos_access_staff_hint')}
                        </div>
                    </div>

                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                            <label style={labelStyle}>{t('pin')}</label>
                            <div style={{ position: 'relative' }}>
                                <input style={{ ...inputStyle, paddingRight: 40 }} type={showPin ? 'text' : 'password'} value={form.pin_code} maxLength={6}
                                    onChange={(e) => set('pin_code', e.target.value)} placeholder={t('six_digit_pin')} />
                                <button type="button" onClick={() => setShowPin(v => !v)}
                                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 0, display: 'flex' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {showPin
                                            ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                                            : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
                                    </svg>
                                </button>
                            </div>
                            {a.has_pin && (
                                <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
                                    {t('pin_blank_keeps_current')}
                                </div>
                            )}
                        </div>
                        <div>
                            <label style={labelStyle}>{t('preferred_language')}</label>
                            <select style={inputStyle} value={form.preferred_language} onChange={(e) => set('preferred_language', e.target.value)}>
                                {LANG_OPTIONS.map(l => <option key={l.value} value={l.value}>{t(l.labelKey)}</option>)}
                            </select>
                        </div>
                    </div>

                </div>
                <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.borderLight}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={secondaryButtonStyle}>{t('cancel')}</button>
                    <button onClick={handleSubmit} disabled={saving}
                        style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1, cursor: saving ? 'default' : 'pointer' }}>
                        {saving ? t('saving') : t('save_changes')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Deactivate confirm modal ────────────────────────────────────────────────

const DeactivateModal = ({ employee, onClose, onConfirmed, t }) => {
    const [reason, setReason] = useState('');
    const [working, setWorking] = useState(false);
    const [error, setError] = useState('');

    const handleConfirm = async () => {
        try {
            setWorking(true);
            setError('');
            await employeeV2Service.softDelete(employee.employee_id, reason || null);
            await onConfirmed();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.detail || t('failed_to_save_employee'));
        } finally {
            setWorking(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
            onKeyDown={(e) => e.key === 'Escape' && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 440, padding: '24px 24px 20px', fontFamily: FONT }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8 }}>{t('deactivate_employee')}</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                    {t('confirm_deactivate_employee')} <strong>{employee.name}</strong>.
                </div>
                {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '10px 14px', fontSize: 13, color: C.redText, marginBottom: 12 }}>{error}</div>}
                <label style={labelStyle}>{t('termination_reason')}</label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                    style={{ ...inputStyle, resize: 'vertical', marginBottom: 16 }}
                    placeholder={t('optional')} />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={secondaryButtonStyle}>{t('cancel')}</button>
                    <button onClick={handleConfirm} disabled={working}
                        style={{ ...primaryButtonStyle, background: C.red, opacity: working ? 0.7 : 1, cursor: working ? 'default' : 'pointer' }}>
                        {working ? t('saving') : t('deactivate')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const PortalEmployeeDetailPage = () => {
    const { employeeId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useLanguage();
    const { getCurrency } = useAuth();
    const currency = getCurrency();
    const isMobile = useIsMobile();

    const activeTab = searchParams.get('tab') || 'personal';

    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editOpen, setEditOpen] = useState(false);
    const [deactivateOpen, setDeactivateOpen] = useState(false);
    const [kebabOpen, setKebabOpen] = useState(false);
    const kebabRef = useRef(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await employeeV2Service.getEmployeeDetail(employeeId);
            setEmployee(data);
        } catch (err) {
            setError(err?.response?.status === 404 ? t('employee_not_found') : t('failed_to_load_employee'));
        } finally {
            setLoading(false);
        }
    }, [employeeId, t]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const handler = (e) => {
            if (kebabRef.current && !kebabRef.current.contains(e.target)) setKebabOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const setTab = (key) => {
        const p = new URLSearchParams(searchParams);
        p.set('tab', key);
        setSearchParams(p, { replace: true });
    };

    const renderTab = () => {
        if (!employee) return null;
        const props = { employee, onRefresh: load, t, currency };
        switch (activeTab) {
            case 'employment': return <TabEmployment {...props} />;
            case 'payroll': return <TabPayroll {...props} />;
            case 'schedule': return <TabSchedule {...props} />;
            case 'access': return <TabAccess {...props} />;
            case 'personal':
            default: return <TabPersonalInfo {...props} />;
        }
    };

    if (loading) {
        return (
            <PortalLayout>
                <div style={{ padding: 24, fontFamily: FONT, color: C.mutedLight, fontSize: 13 }}>{t('loading')}</div>
            </PortalLayout>
        );
    }

    if (error || !employee) {
        return (
            <PortalLayout>
                <div style={{ padding: 24, fontFamily: FONT }}>
                    <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 8, padding: '12px 16px', fontSize: 13, color: C.redText, marginBottom: 16 }}>
                        {error || t('employee_not_found')}
                    </div>
                    <button onClick={() => navigate('/portal/employees')} style={secondaryButtonStyle}>
                        {t('back_to_employees')}
                    </button>
                </div>
            </PortalLayout>
        );
    }

    const payFreqKey = PAY_FREQUENCY_KEYS[employee.compensation?.pay_frequency];
    const employmentKey = EMPLOYMENT_KEYS[employee.employment?.employment_type];
    const isCashier = !!employee.access?.is_cashier;
    const isActive = (employee.status || '').toLowerCase() === 'active';

    const EDIT_LABELS = {
        personal: 'edit_personal_info',
        employment: 'edit_employment',
        access: 'edit_access',
    };
    const editLabel = EDIT_LABELS[activeTab];
    const showEditBtn = !!editLabel;

    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT, margin: isMobile ? '-16px -16px 0' : '-28px -28px 0', display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 52px)' }}>

                {/* Sticky header block (back row + profile + tab bar) */}
                <div style={{ position: 'sticky', top: 0, zIndex: 20, background: C.bg, borderBottom: `1px solid ${C.border}` }}>

                    {/* Back row */}
                    <div style={{ padding: isMobile ? '14px 16px 0' : '16px 28px 0' }}>
                        <button onClick={() => navigate('/portal/employees')}
                            style={{ background: 'none', border: 'none', color: C.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = C.text)}
                            onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
                            </svg>
                            {t('back_to_employees')}
                        </button>
                    </div>

                    {/* Profile row */}
                    <div style={{ padding: isMobile ? '12px 16px 0' : '14px 28px 0', display: 'flex', alignItems: 'flex-start', gap: isMobile ? 12 : 16, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                        <div style={{ width: isMobile ? 52 : 64, height: isMobile ? 52 : 64, borderRadius: '50%', background: avatarColor(employee.name), display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontSize: isMobile ? 18 : 22, fontWeight: 700, flexShrink: 0 }}>
                            {initials(employee.name)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 2 }}>{employee.name}</div>
                            <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>
                                {employee.role}{employee.department ? ` · ${employee.department}` : ''}
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: isActive ? C.greenLight : '#f1f5f9', color: isActive ? '#166534' : C.muted }}>
                                    {isActive ? t('active') : t('inactive')}
                                </span>
                                {isCashier && (
                                    <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: C.greenLight, color: C.green }}>{t('cashier')}</span>
                                )}
                                {employmentKey && (
                                    <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: C.blueLight, color: C.blue }}>{t(employmentKey)}</span>
                                )}
                                {payFreqKey && (
                                    <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: C.purpleLight, color: C.purple }}>{t(payFreqKey)}</span>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexShrink: 0 }}>
                            {showEditBtn && (
                                <button onClick={() => setEditOpen(true)}
                                    style={primaryButtonStyle}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = C.greenDark)}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = C.green)}>
                                    {t(editLabel)}
                                </button>
                            )}
                            <div ref={kebabRef} style={{ position: 'relative' }}>
                                <button onClick={() => setKebabOpen(v => !v)}
                                    style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', cursor: 'pointer', color: C.muted, display: 'flex', alignItems: 'center' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" /></svg>
                                </button>
                                {kebabOpen && (
                                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, minWidth: 200, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden', zIndex: 30 }}>
                                        <button onClick={() => { setKebabOpen(false); setTab('access'); }}
                                            style={{ width: '100%', padding: '10px 14px', fontSize: 13, color: C.text, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
                                            onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
                                            {t('reset_pin')}
                                        </button>
                                        <button onClick={() => { setKebabOpen(false); setTab('payroll'); }}
                                            style={{ width: '100%', padding: '10px 14px', fontSize: 13, color: C.text, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
                                            onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
                                            {t('view_payroll')}
                                        </button>
                                        {isActive && (
                                            <button onClick={() => { setKebabOpen(false); setDeactivateOpen(true); }}
                                                style={{ width: '100%', padding: '10px 14px', fontSize: 13, color: C.redText, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
                                                onMouseEnter={(e) => (e.currentTarget.style.background = C.redLight)}
                                                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
                                                {t('deactivate_employee')}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tab bar */}
                    {isMobile ? (
                        <div style={{ padding: '12px 16px 0' }}>
                            <MobileTabSelect
                                tabs={TABS.map(tab => ({ key: tab.key, label: t(tab.translationKey) }))}
                                active={activeTab}
                                onChange={setTab}
                                label={t('tab') || 'Tab'}
                            />
                        </div>
                    ) : (
                    <div style={{ padding: isMobile ? '12px 16px 0' : '16px 28px 0', display: 'flex', gap: 4, overflowX: 'auto', ...(isMobile ? { whiteSpace: 'nowrap', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch' } : {}) }}>
                        {TABS.map(tab => {
                            const active = activeTab === tab.key;
                            return (
                                <button key={tab.key} onClick={() => setTab(tab.key)}
                                    style={{
                                        background: 'none', border: 'none', padding: '10px 14px', flexShrink: 0,
                                        fontSize: 13, fontWeight: active ? 700 : 600,
                                        color: active ? C.green : C.muted, cursor: 'pointer',
                                        borderBottom: active ? `2px solid ${C.green}` : '2px solid transparent',
                                        marginBottom: -1, transition: 'color 0.15s', whiteSpace: 'nowrap',
                                        fontFamily: 'inherit',
                                    }}
                                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = C.text; }}
                                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = C.muted; }}>
                                    {t(tab.translationKey)}
                                </button>
                            );
                        })}
                    </div>
                    )}
                </div>

                {/* Tab content */}
                <div style={{ flex: 1, padding: isMobile ? '16px' : '24px 28px', background: C.bg }}>
                    {renderTab()}
                </div>
            </div>

            {editOpen && activeTab === 'personal' && (
                <EditPersonalInfoModal employee={employee} onClose={() => setEditOpen(false)} onSaved={load} t={t} />
            )}
            {editOpen && activeTab === 'employment' && (
                <EditEmploymentModal employee={employee} onClose={() => setEditOpen(false)} onSaved={load} t={t} />
            )}
            {editOpen && activeTab === 'access' && (
                <EditAccessModal employee={employee} onClose={() => setEditOpen(false)} onSaved={load} t={t} />
            )}
            {deactivateOpen && (
                <DeactivateModal employee={employee} onClose={() => setDeactivateOpen(false)} onConfirmed={load} t={t} />
            )}
        </PortalLayout>
    );
};

export default PortalEmployeeDetailPage;
